import { useState } from "react";

import { Button, IconName, Menu, MenuItem } from "@blueprintjs/core";

import { Popover } from "@/components/helpers/ui/blueprint-overrides/Popover";
import {
  AttendingStatus,
  MeetingAttendeesDocument,
  useSaveAttendingStatusMutation,
  FullMeetingQuery,
} from "@/graphql";

import { AttendeeAttendingStatus } from "./AttendingStatusIcon";

export type MiniAttendee = NonNullable<
  NonNullable<FullMeetingQuery["meeting"]>["attendee"]
>;

interface AttendingDropdownButtonInnerProps {
  attendingStatus?: AttendingStatus | null;
  onChange?: (status: AttendingStatus) => void;
  loading?: boolean;
}

function AttendingDropdownButtonInner({
  attendingStatus,
  onChange,
  loading,
}: AttendingDropdownButtonInnerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const activeStatus = attendingStatus || AttendingStatus.MayBe;
  const activeIcon = AttendeeAttendingStatus[activeStatus];

  const menuContent = (
    <Menu>
      {[
        AttendingStatus.Attending,
        AttendingStatus.NotAttending,
        AttendingStatus.MayBe,
      ]
        .map((status) => AttendeeAttendingStatus[status])
        .map((iconMap) => (
          <MenuItem
            active={attendingStatus === iconMap.status}
            icon={iconMap.icon as IconName}
            intent={iconMap.intent}
            key={iconMap.status}
            text={iconMap.text}
            onClick={() => {
              onChange?.(iconMap.status);
              setIsOpen(false);
            }}
          />
        ))}
    </Menu>
  );

  return (
    <Popover
      content={menuContent}
      placement="bottom"
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <Button
        outlined={true}
        icon={activeIcon.icon as IconName}
        intent={activeIcon.intent}
        text={activeIcon.text}
        loading={loading}
        onClick={() => setIsOpen(!isOpen)}
      />
    </Popover>
  );
}

interface AttendingDropdownButtonProps {
  attendee: MiniAttendee;
}

export function AttendingDropdownButton({
  attendee,
}: AttendingDropdownButtonProps) {
  const [saveAttendingStatus, { loading }] = useSaveAttendingStatusMutation({
    refetchQueries: [
      {
        query: MeetingAttendeesDocument,
        variables: { meetingId: attendee.meetingId, start: 0, limit: 4 },
      },
    ],
  });

  const updateAttendingStatus = (status: AttendingStatus) => {
    if (typeof saveAttendingStatus === "function") {
      return saveAttendingStatus({
        variables: { attendeeId: attendee?.id, attendingStatus: status },
      });
    }
  };
  return (
    <AttendingDropdownButtonInner
      onChange={(status) => {
        updateAttendingStatus(status);
      }}
      attendingStatus={attendee?.attendingStatus}
      loading={loading}
    />
  );
}
